import React, { useState } from 'react';
import type { AiQueryResponse } from '../types';

interface Props {
  onRunAiQuery: (query: string, roleContext: string) => Promise<AiQueryResponse>;
  onOpenPreviewAtAnchor: (fileId: string, anchor: string) => void;
}

export const AiCitationsView: React.FC<Props> = ({
  onRunAiQuery,
  onOpenPreviewAtAnchor,
}) => {
  const [queryText, setQueryText] = useState(
    'Какие документы нужны для научной стажировки докторанта?'
  );
  const [roleContext, setRoleContext] = useState('SYSTEM_OWNER');
  const [isLoading, setIsLoading] = useState(false);
  const [aiResult, setAiResult] = useState<AiQueryResponse | null>(null);

  const presets = [
    'Какие документы нужны для научной стажировки докторанта?',
    'Какой регламент сдачи практики по Smart ENU?',
    'Какие документы нужны для утверждения темы и консультантов?',
    'Какие пороговые значения оригинальности и антиплагиата установлены?',
  ];

  const handleQuery = async (queryToRun?: string) => {
    const q = queryToRun || queryText;
    if (!q.trim()) return;
    setIsLoading(true);

    try {
      const res = await onRunAiQuery(q, roleContext);
      setAiResult(res);
    } catch {
      setAiResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="ai-citations-view" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div className="ai-query-container">
        <div>
          <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#f8fafc', margin: '0 0 0.35rem 0' }}>
            Проверяемые ответы консультанта и цитирование первоисточников
          </h3>
          <p style={{ color: '#94a3b8', fontSize: '0.88rem', margin: 0 }}>
            Super AI и ролевые ассистенты используют строго разрешённые актуальные документы.
            Каждый ответ содержит кликабельный перечень фактически использованных источников и точный version_id.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '0.82rem', color: '#94a3b8' }}>Ролевой контекст запроса:</span>
          <select
            className="library-select"
            value={roleContext}
            onChange={(e) => setRoleContext(e.target.value)}
          >
            <option value="SYSTEM_OWNER">Владелец системы</option>
            <option value="DEPARTMENT_HEAD">DEPARTMENT_HEAD (Заведующий кафедрой)</option>
            <option value="ADVISOR">ADVISOR (Научный консультант)</option>
            <option value="LEARNER">Докторант</option>
            <option value="OPERATIONS_PARTNER">OPERATIONS_PARTNER (Операционный партнёр)</option>
          </select>
        </div>

        <div className="ai-query-input-box">
          <input
            type="text"
            className="ai-query-input"
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            placeholder="Задайте академический вопрос по регламентам или документам..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleQuery();
            }}
          />
          <button
            type="button"
            className="ai-query-submit-btn"
            onClick={() => handleQuery()}
            disabled={isLoading}
          >
            {isLoading ? 'Анализ источников...' : 'Спросить AI'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.78rem', color: '#64748b', alignSelf: 'center' }}>
            Примеры вопросов:
          </span>
          {presets.map((preset) => (
            <button
              key={preset}
              type="button"
              className="category-pill-btn"
              onClick={() => {
                setQueryText(preset);
                handleQuery(preset);
              }}
            >
              {preset}
            </button>
          ))}
        </div>
      </div>

      {aiResult && (
        <div className="ai-response-card" data-testid="ai-response-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
            <span style={{ fontSize: '0.82rem', color: '#38bdf8', fontWeight: 600 }}>
              Ответ AI-ассистента ({aiResult.role_context})
            </span>
            <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
              Проверяемость: {aiResult.is_verifiable ? '✓ Полная опора на источники' : 'Ограниченная опора'}
            </span>
          </div>

          <div className="ai-response-text">{aiResult.answer}</div>

          <div className="ai-sources-block">
            <div className="ai-sources-title">
              Источники ответа ({aiResult.citations.length})
            </div>

            {aiResult.citations.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
                В рамках разрешённой роли {aiResult.role_context} доступных первоисточников не найдено.
              </p>
            ) : (
              <div className="ai-sources-grid">
                {aiResult.citations.map((cite) => (
                  <div key={cite.file_id} className="ai-source-card" data-testid={`ai-citation-${cite.file_id}`}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#f8fafc' }}>
                        {cite.display_name}
                      </span>
                      <div style={{ display: 'flex', gap: '0.3rem' }}>
                        <span className="qa-version-pill">{cite.version_no}</span>
                        <span className="qa-current-pill">Актуальная</span>
                      </div>
                    </div>

                    <div style={{ fontSize: '0.78rem', color: '#38bdf8' }}>
                      Фрагмент: {cite.citation_anchor}
                    </div>

                    <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineClamp: 2, overflow: 'hidden' }}>
                      {cite.excerpt_text}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.35rem' }}>
                      <button
                        type="button"
                        className="btn-open-preview"
                        style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem' }}
                        onClick={() => onOpenPreviewAtAnchor(cite.file_id, cite.citation_anchor)}
                      >
                        Открыть / Посмотреть
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
